import { AnimatePresence, motion, useInView, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "motion/react";
import Lenis from "lenis";
import { FormEvent, useEffect, useRef, useState } from "react";

const A = "/assets/source/";

// Where "Work" and "See Our Work" point, and where the contact form posts. Both live on the portfolio service.
const PORTFOLIO_URL = "https://portfolio.markandtingagency.com/";
const CONTACT_ENDPOINT = "https://portfolio.markandtingagency.com/api/contact";
// The hero gallery plays light clips of the portfolio's Content Production videos.
const PORTFOLIO_ITEMS = new URL("api/portfolio/items", PORTFOLIO_URL.endsWith("/") ? PORTFOLIO_URL : `${PORTFOLIO_URL}/`).toString();

const nav = [
  ["Home", "header"], ["Services", "services"], ["Benefits", "benefits"],
  ["Pricing", "pricing"], ["Reviews", "reviews"], ["FAQs", "faqs"],
  ["Contact", "contact"], ["Onboarding", "onboarding"], ["Team", "team"],
];

const gallery = [
  "AIoOfGAMorkJjQmj2tX7TrOu3iA.jpg", "09ucYxzynkNazR7K8tqfYJOIazs.jpeg",
  "Q5593UengmjCPDObjrtmRanCt1I.jpg", "5hiNETax6RJrOnGME3MkBU2Nk.jpg",
  "YuOhQNA0BwXAFO9jltpX9YP2rPo.jpg", "wmovVbWoFmeH7kNLQxdXnazYzaw.jpg",
  "VhNFGVmT7SnXpfq927JltZbV1A.jpg", "UAXYquxR6CJELtIQM8umss9GDl4.jpg",
];

const clientLogos = [
  "mhBIUYHxEZPHnQn0U2cNkOi9Y.png", "tVxnpIY5u85GFHTlJqgEwhJ3RXE.png", "iTefnFH1L6KXpJ9VXCIiPSoQiQ.png",
  "96CYr0Q5TDioIe9otmo0V2JOLTM.png", "YZbP5QywErGwdOrAzdcNqisCBg.png", "NbDlZpTSaw7eVHoKjH3XQoMDsY.png",
  "GBlCBMjmbaqIvY7N0dyrN0pt3QI.png", "jLh0zi39jTEAzJ73hnkhcMf39yA.png", "R5EmbQoOLIDxwYVhXRREGducpCY.png", "OukPKtMUdyA3qtEkECMq51THvIk.png",
];

const services = [
  {
    badge: "Video Production",
    title: "Short-Form Video & Content Production",
    text: "High-retention vertical videos, professional drone footage, and visual storytelling built for Instagram, TikTok, and YouTube Shorts.",
    bullets: ["Monthly filming with professional camera gear", "High-tempo editing, typography and sound design", "4K drone footage for exteriors and commercial spaces", "Content calendars planned around buyer intent"],
    visual: "video",
    work: "video",
    fallback: "AIoOfGAMorkJjQmj2tX7TrOu3iA.jpg",
  },
  {
    badge: "Paid Media",
    title: "High-Converting Paid Advertising",
    text: "Targeted campaigns across Meta and Google that place your offer in front of ready-to-buy local and regional customers.",
    bullets: ["Meta Ads across feeds, stories and reels", "Google Search and local campaigns", "Direct copywriting and ongoing creative testing", "Conversion tracking and clear performance reporting"],
    visual: "ads",
    work: "ads",
    fallback: "qGzVlzhwMiju9erONiglTNiyct4.png",
  },
  {
    badge: "AI & Systems",
    title: "Growth Systems & AI Implementation",
    text: "Custom lead capture workflows, CRM pipelines, and automated assistants that help your team respond while prospects are still interested.",
    bullets: ["Automated WhatsApp and Instagram DM responses", "CRM pipelines with automated follow-ups", "AI assistants that answer and qualify leads day or night", "Lead routing directly to your sales team"],
    visual: "automation",
    work: "automation",
    fallback: "qGzVlzhwMiju9erONiglTNiyct4.png",
  },
  {
    badge: "Web & CRO",
    title: "Websites & Conversion Landing Pages",
    text: "Custom web experiences designed to tell your story, strengthen organic visibility, and turn visits into qualified enquiries.",
    bullets: ["Fast, mobile-first builds", "Clear conversion and booking pathways", "A clean technical SEO foundation", "Contact and quote capture connected to your workflow"],
    visual: "web",
    work: "web",
    fallback: "qGzVlzhwMiju9erONiglTNiyct4.png",
  },
  {
    badge: "Brand Strategy",
    title: "Brand Strategy & Visual Identity",
    text: "Professional branding and creative assets that make your business look clear, consistent, and worth choosing.",
    bullets: ["Visual identities, logos, palettes and guidelines", "Creative design for campaigns and print", "Positioning that stands out in a crowded market", "A consistent presence across every touchpoint"],
    visual: "branding",
    work: "branding",
    fallback: "AIoOfGAMorkJjQmj2tX7TrOu3iA.jpg",
  },
  {
    badge: "Real Estate",
    title: "Real Estate Media",
    text: "Listing walkthroughs, aerial property media, and personal brand content for realtors, builders, and properties that need to stand out.",
    bullets: ["4K property walkthrough videos", "HDR interior and exterior photography", "Drone footage that shows context and scale", "Agent content that builds local authority"],
    visual: "real-estate",
    work: "video",
    fallback: "YuOhQNA0BwXAFO9jltpX9YP2rPo.jpg",
  },
];

const benefits = [
  ["◎", "A Single Integrated System", "Your creative, ads, websites, and automations work together from day one — without coordinating separate vendors."],
  ["⌁", "Multilingual Market Advantage", "We build campaigns in English and Spanish, with experience reaching German- and Italian-speaking audiences."],
  ["↗", "Automations That Close Deals", "The first business to reply usually wins the lead. Our WhatsApp and DM automations answer instantly, qualify prospects, and book calls while they are still interested."],
  ["◇", "Direct Senior Partnership", "Work directly with the founders and the people who make your content, ads, and systems. No middle layers diluting your feedback."],
  ["▰", "High-Impact Creative Studio", "Our team handles scripting, professional production, drone footage, design, and post-production to keep your brand looking consistent."],
  ["▥", "Focused On Your Bottom Line", "We connect creative and distribution to the enquiries, appointments, walk-ins, and sales opportunities that move your business."],
];

const comparisonRows = [
  ["Content, ads, web and automation under one strategy", true, false, false, false],
  ["Our own crew for video, photo and drone", true, false, true, false],
  ["Automated lead follow-up (WhatsApp, DMs, CRM)", true, false, false, false],
  ["Campaigns in English and Spanish", true, false, false, false],
  ["No long-term contracts", true, false, true, false],
  ["Direct line to the people doing the work", true, true, true, false],
  ["No hiring, training or managing staff", true, false, false, true],
  ["Focused on leads and sales, not just likes", true, true, false, false],
] as const;

const reviews = [
  ["Mark & Ting has been a great partner for Tu Clínica Familiar. Their work goes beyond marketing — they bring strategy, creativity, and systems that help a business grow with more structure and consistency.", "Esteban Torres", "Co Founder TCF", "eU0eE1uH2s48zEZ2CvBgNbNmM1Y.jpeg"],
  ["What we value most is that they don’t just create content — they think about how to position the business, improve visibility, and build systems that support long-term growth.", "Gerardo Romero", "Romeros Auto Center & Superior Founder", "OS4kQqWVoAz7BhW5muuEOyaB6s.jpeg"],
  ["Their work has helped us improve our image, stay more consistent, and approach growth in a more professional and strategic way.", "Marcos Moreno", "CEO & Founder TIPS Plant Services", "LP3tY1VU63Loeiw2m8ahNTubMwQ.jpeg"],
  ["They understand how important trust and positioning are for businesses in real estate and lending. Their support has helped us strengthen our presence.", "Kimian Ng", "Senior Mortgage Banker Flat Branch", "I4eD4HMB8QiaTaGTRrT4nLC9Y8.jpg"],
];

const faqs = [
  ["How does working with Mark & Ting start?", "We start with a discovery conversation to understand your business, goals, and where you may be losing visibility, leads, or opportunities. From there, we recommend the right growth system."],
  ["What types of businesses do you work with?", "We work primarily with service-based businesses and realtors looking to generate more clients through content, ads, and automation systems."],
  ["Do I need to commit to a long-term contract?", "No. We focus on real partnerships, but we don’t believe in locking businesses into something that doesn’t create value."],
  ["What services can be included in my package?", "Depending on your needs, your package can include content creation, social media management, Meta Ads, Google Ads, landing pages, branding, websites, automation, CRM setup, and lead follow-up systems."],
  ["Do you only help with social media?", "No. We help businesses grow with content, ads, and automation that work together as one system."],
  ["How do I know which package is right for my business?", "That’s exactly what the strategy call is for. We’ll help identify what makes sense based on your stage and goals."],
];

const steps = [
  ["Discovery & Alignment", "We begin with a strategic conversation to understand your business, identify opportunities, and determine if we’re the right fit."],
  ["Onboarding & Access", "We collect everything needed to build the right system — goals, offer, platforms, branding, and essential access."],
  ["Strategy & System Setup", "We define the strategy and build the technical infrastructure needed to attract, capture, and convert leads."],
  ["Launch & Execution", "We walk you through the setup, align on execution, launch, and move into ongoing optimization and growth."],
];

const team = [
  {
    name: "Yedan Herrera",
    role: "Founder",
    image: "/assets/team/yedan.webp",
    bio: "An innovative leader with a background in Financial Direction from Universidad Anáhuac CDMX and experience at Nu Bank. After studying in Rome and earning recognition at the Unilever Future Leaders League in London, he now builds systems that drive growth and empower the business community.",
  },
  {
    name: "Yury Herrera",
    role: "Co-Founder",
    image: "/assets/team/yury.webp",
    bio: "A growth-driven leader with a background in International Business from UIW and an MBA from MCI. With experience in Germany, Austria and finance at Grupo Salinas, he focuses on systems that help businesses scale sustainably.",
  },
  {
    name: "Israel",
    role: "Programmer",
    image: "/assets/team/israel.webp",
    bio: "The wizard behind the code. He builds distinctive websites engineered for speed, conversion and strong organic visibility.",
  },
  {
    name: "Bárbara",
    role: "Community Manager",
    image: "/assets/team/barbara.webp",
    bio: "A digital marketing enthusiast who believes social media is for connecting, not just posting. Her approach puts strategy over gimmicks and turns online engagement into loyal customers.",
  },
  {
    name: "Gastón",
    role: "Video Editor",
    image: "/assets/team/gaston.webp",
    bio: "The creative eye behind our clients' scroll-stopping videos. He edits dynamic, high-retention content designed to make an immediate impact and support measurable business results.",
  },
  {
    name: "Genesys",
    role: "Graphic Designer",
    image: "/assets/team/genesys.webp",
    bio: "A graphic designer specializing in high-performance creative assets. She makes sure websites, ad creative and social channels command attention and support conversion.",
  },
];

function Reveal({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const reduce = useReducedMotion();
  return <motion.div className={className} initial={reduce ? false : { opacity: 0, y: 34, scale: .985, filter: "blur(8px)" }} whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }} viewport={{ once: true, margin: "-10%" }} transition={{ duration: .78, delay, ease: [.22, 1, .36, 1] }}>{children}</motion.div>;
}

function TiltCard({ children, className = "", lift = 0 }: { children: React.ReactNode; className?: string; lift?: number }) {
  const reduce = useReducedMotion();
  const rotateX = useSpring(useMotionValue(0), { stiffness: 180, damping: 22, mass: .7 });
  const rotateY = useSpring(useMotionValue(0), { stiffness: 180, damping: 22, mass: .7 });
  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (reduce || event.pointerType === "touch") return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - .5;
    const y = (event.clientY - rect.top) / rect.height - .5;
    rotateY.set(x * 5);
    rotateX.set(y * -5);
  };
  const onPointerLeave = () => { rotateX.set(0); rotateY.set(0); };
  return <motion.article className={className} style={{ rotateX, rotateY, transformPerspective: 1100 }} onPointerMove={onPointerMove} onPointerLeave={onPointerLeave} whileHover={{ y: lift, scale: 1.008 }} transition={{ type: "spring", stiffness: 180, damping: 20 }}>{children}</motion.article>;
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  return <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} aria-hidden="true" />;
}

function Pill({ children }: { children: React.ReactNode }) {
  const reduce = useReducedMotion();
  const text = typeof children === "string" ? children : "";
  const [count, setCount] = useState(reduce ? text.length : 0);
  const [active, setActive] = useState(Boolean(reduce));
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (reduce) {
      setActive(true);
      setCount(text.length);
      return;
    }
    const node = ref.current;
    if (!node || !text) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setActive(true);
        observer.disconnect();
      }
    }, { threshold: .65 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [reduce, text]);

  useEffect(() => {
    if (!active || reduce || count >= text.length) return;
    const timer = window.setTimeout(() => setCount(value => value + 1), 42);
    return () => window.clearTimeout(timer);
  }, [active, count, reduce, text.length]);

  return <span ref={ref} className="pill" aria-label={text || undefined}><i /><span className={`pill-label ${active && count < text.length ? "typing" : ""}`}>{text ? text.slice(0, count) : children}</span><i /></span>;
}

function Button({ href, children, light = false }: { href: string; children: React.ReactNode; light?: boolean }) {
  return <motion.a className={`button ${light ? "button-light" : "button-dark"} ${href === "#contact" ? "button-cta" : ""}`} href={href} whileHover={{ y: -3, scale: 1.015 }} whileTap={{ scale: .98 }}><span>{children}</span><b className="button-icon" aria-hidden="true"><i /><i /><i /><i /></b></motion.a>;
}

function SectionTitle({ eyebrow, title, children }: { eyebrow: string; title: React.ReactNode; children?: React.ReactNode }) {
  return <div className="section-head"><Reveal><Pill>{eyebrow}</Pill><h2>{title}</h2></Reveal>{children && <Reveal className="section-intro" delay={.08}>{children}</Reveal>}</div>;
}

// 5 logos at a time; every few seconds all 5 change together to 5 that were not on screen, in a random order.
function RandomLogoGrid() {
  const reduce = useReducedMotion();
  const [logos, setLogos] = useState(() => shuffled(clientLogos).slice(0, 5));

  useEffect(() => {
    if (reduce) return;
    const timer = window.setInterval(() => {
      setLogos(current => {
        const fresh = shuffled(clientLogos.filter(logo => !current.includes(logo)));
        return fresh.length >= 5 ? fresh.slice(0, 5) : [...fresh, ...shuffled(current)].slice(0, 5);
      });
    }, 4500);
    return () => window.clearInterval(timer);
  }, [reduce]);

  return <div className="logo-grid">{logos.map((src, index) => <div className="logo-slot" key={index}>
    <AnimatePresence mode="wait" initial={false}>
      <motion.img key={src} src={`/assets/logos/${src.replace(/\.png$/, ".webp")}`} alt="Client brand" initial={{ opacity: 0, scale: .94 }} animate={{ opacity: 1, scale: 1, transition: { duration: .45, delay: index * .07 } }} exit={{ opacity: 0, scale: 1.04, transition: { duration: .3, delay: index * .07 } }} />
    </AnimatePresence>
  </div>)}</div>;
}

function ServiceVideo({ clips, fallback, title }: { clips: Clip[]; fallback: string; title: string }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const inView = useInView(frameRef, { amount: .3 });
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const clip = clips[index % Math.max(clips.length, 1)];
  const clipIds = clips.map(item => item.id).join("|");
  const active = Boolean(clip && inView && !reduce);

  useEffect(() => setIndex(0), [clipIds]);
  useEffect(() => {
    if (!active || clips.length < 2) return;
    const timer = window.setInterval(() => setIndex(current => (current + 1) % clips.length), 7000);
    return () => window.clearInterval(timer);
  }, [active, clips.length]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (active) video.play().catch(() => undefined);
    else {
      video.pause();
      video.currentTime = 0;
      video.removeAttribute("src");
      video.load();
    }
  }, [active, clip?.id]);

  return <div ref={frameRef} className="service-image-rotator service-image-video-frame">
    {!clip ? <img className="service-image-main" src={`${A}${fallback}`} alt={title} /> : <AnimatePresence initial={false}>
      <motion.video key={clip.id} ref={videoRef} className="service-image-main" src={active ? clip.mp4 : undefined} poster={clip.poster} muted loop playsInline preload="none" aria-label={title}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .6 }} onCanPlay={() => active && videoRef.current?.play().catch(() => undefined)} onError={() => clips.length > 1 && setIndex(current => (current + 1) % clips.length)} />
    </AnimatePresence>}
  </div>;
}

function AutomationMockup() {
  return <div className="automation-mockup" aria-label="Example automated WhatsApp conversation">
    <div className="automation-head"><span>MT</span><div><strong>Mark&amp;Ting Assistant</strong><small>Online</small></div><b>•••</b></div>
    <div className="automation-chat">
      <Reveal className="message message-client"><p>Hi! I would like to grow my business.</p><small>10:24</small></Reveal>
      <Reveal className="message message-bot" delay={.18}><p>Great — what would you like help with first?</p><small>10:24 ✓✓</small></Reveal>
      <Reveal className="message message-client" delay={.34}><p>More qualified leads.</p><small>10:25</small></Reveal>
      <Reveal className="message message-bot" delay={.5}><p>Perfect. I can book a strategy call for tomorrow at 11:00 AM.</p><small>10:25 ✓✓</small></Reveal>
    </div>
    <div className="automation-input"><span>Message</span><b>↗</b></div>
  </div>;
}

// Branding pieces are flat images with their own background and the logo at any size or corner. Each one is redrawn
// once, in the browser, into a 4:5 card: the logo is found by its pixels (like the portfolio does) and centred at a
// steady size on its own background colour (dark or light for transparent logos). null = could not read it.
type FittedLogo = { src: string; background: string };
const fittedLogos = new Map<string, Promise<FittedLogo | null>>();
function fitLogo(url: string): Promise<FittedLogo | null> {
  let job = fittedLogos.get(url);
  if (!job) {
    job = new Promise<FittedLogo | null>(resolve => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.decoding = "async";
      img.onerror = () => resolve(null);
      img.onload = () => {
        try {
          const ratio = Math.min(360 / img.naturalWidth, 360 / img.naturalHeight, 1);
          const w = Math.max(1, Math.round(img.naturalWidth * ratio));
          const h = Math.max(1, Math.round(img.naturalHeight * ratio));
          const probe = document.createElement("canvas");
          probe.width = w;
          probe.height = h;
          const pc = probe.getContext("2d", { willReadFrequently: true })!;
          pc.drawImage(img, 0, 0, w, h);
          const { data } = pc.getImageData(0, 0, w, h);
          const corner = [0, 0, 0, 0];
          let samples = 0;
          const size = Math.max(2, Math.round(Math.min(w, h) * .04));
          for (const [cx, cy] of [[0, 0], [w - size, 0], [0, h - size], [w - size, h - size]]) {
            for (let y = cy; y < cy + size; y++) for (let x = cx; x < cx + size; x++) {
              const i = (y * w + x) * 4;
              for (let k = 0; k < 4; k++) corner[k] += data[i + k];
              samples++;
            }
          }
          const bg = corner.map(v => v / samples);
          const transparent = bg[3] < 128;
          let minX = w, minY = h, maxX = -1, maxY = -1, count = 0, light = 0;
          for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            if (data[i + 3] <= 18) continue;
            if (!transparent && Math.hypot(data[i] - bg[0], data[i + 1] - bg[1], data[i + 2] - bg[2]) <= 42) continue;
            count++;
            if ((data[i] + data[i + 1] + data[i + 2]) / 3 > 184) light++;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
          if (maxX < 0) return resolve(null);
          const background = transparent
            ? (light / count > .58 ? "#171717" : "#ececea")
            : `rgb(${Math.round(bg[0])}, ${Math.round(bg[1])}, ${Math.round(bg[2])})`;
          // Crop in the original image, with a hair of margin so anti-aliased edges stay.
          const scaleBack = img.naturalWidth / w;
          const sx = Math.max(0, (minX - 1) * scaleBack);
          const sy = Math.max(0, (minY - 1) * scaleBack);
          const sw = Math.min(img.naturalWidth - sx, (maxX - minX + 3) * scaleBack);
          const sh = Math.min(img.naturalHeight - sy, (maxY - minY + 3) * scaleBack);
          const out = document.createElement("canvas");
          out.width = 800;
          out.height = 1000;
          const oc = out.getContext("2d")!;
          oc.fillStyle = background;
          oc.fillRect(0, 0, 800, 1000);
          const fit = Math.min(800 * .72 / sw, 1000 * .5 / sh);
          const dw = sw * fit;
          const dh = sh * fit;
          oc.imageSmoothingQuality = "high";
          oc.drawImage(img, sx, sy, sw, sh, (800 - dw) / 2, (1000 - dh) / 2, dw, dh);
          out.toBlob(blob => resolve(blob ? { src: URL.createObjectURL(blob), background } : null), "image/webp", .9);
        } catch {
          resolve(null); // a canvas the browser would not let us read
        }
      };
      // Its own cache entry: an earlier plain load of the same URL has no CORS headers.
      img.src = `${url}${url.includes("?") ? "&" : "?"}fit=1`;
    });
    fittedLogos.set(url, job);
  }
  return job;
}

function ServiceVisual({ service, items, videoIndex }: { service: typeof services[number]; items: PortfolioItem[]; videoIndex: number }) {
  if (service.visual === "automation") return <AutomationMockup />;

  const frameRef = useRef<HTMLDivElement>(null);
  const inView = useInView(frameRef, { amount: .3 });
  const reduce = useReducedMotion();
  const seed = useRef(Math.random() * 10_000).current;
  const [index, setIndex] = useState(videoIndex);
  const rank = (item: PortfolioItem) => {
    const value = Math.sin(item.id * 12.9898 + seed) * 43758.5453;
    return value - Math.floor(value);
  };
  const allVideos = items.filter(item => item.category === "video" && item.clip && item.clipPoster);
  const realEstateMatches = allVideos.filter(item => /realty|kimian|empire title|sivira/i.test((item as PortfolioItem & { client?: string }).client ?? ""));
  const realEstatePool = realEstateMatches.length >= 2 ? realEstateMatches : allVideos.filter(item => item.id % 2 !== 0);
  const realEstateIds = new Set(realEstatePool.map(item => item.id));
  const videoPool = allVideos.filter(item => !realEstateIds.has(item.id));
  const sourceVideos = service.visual === "real-estate" ? realEstatePool : videoPool;
  const clips = [...sourceVideos].sort((a, b) => rank(a) - rank(b)).map(item => ({
    id: item.youtubeId ?? String(item.id),
    mp4: new URL(item.clip!, PORTFOLIO_ITEMS).toString(),
    poster: new URL(item.clipPoster!, PORTFOLIO_ITEMS).toString(),
  }));

  if (service.visual === "video" || service.visual === "real-estate") return <ServiceVideo clips={clips} fallback={service.fallback} title={service.title} />;

  const imagePool = items.filter(item => item.category === service.visual && item.image).sort((a, b) => rank(a) - rank(b)).slice(0, service.visual === "branding" ? 8 : undefined);
  const current = imagePool[index % Math.max(imagePool.length, 1)];
  const image = current?.image ? new URL(current.image, PORTFOLIO_ITEMS).toString() : `${A}${service.fallback}`;
  const imageIds = imagePool.map(item => item.id).join("|");
  const branding = service.visual === "branding";
  const [fitted, setFitted] = useState<Record<string, FittedLogo | null>>({});

  useEffect(() => setIndex(0), [imageIds]);
  useEffect(() => {
    if (!branding || image in fitted) return;
    let cancelled = false;
    fitLogo(image).then(result => { if (!cancelled) setFitted(done => ({ ...done, [image]: result })); });
    return () => { cancelled = true; };
  }, [branding, image]);
  useEffect(() => {
    if (!inView || reduce || imagePool.length < 2) return;
    let cancelled = false;
    let timer = 0;
    const next = imagePool[(index + 1) % imagePool.length];
    const nextUrl = new URL(next.image!, PORTFOLIO_ITEMS).toString();
    const advance = () => { if (!cancelled) timer = window.setTimeout(() => setIndex(currentIndex => (currentIndex + 1) % imagePool.length), 4500); };
    if (branding) {
      // The next logo is fitted before its turn, so it fades in already centred.
      fitLogo(nextUrl).then(result => { if (!cancelled) { setFitted(done => ({ ...done, [nextUrl]: result })); advance(); } });
    } else {
      const nextImage = new Image();
      nextImage.onload = advance;
      nextImage.onerror = () => {
        if (!cancelled) setIndex(currentIndex => (currentIndex + 1) % imagePool.length);
      };
      nextImage.src = nextUrl;
    }
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [inView, reduce, index, imagePool.length]);

  const skipFailed = () => imagePool.length > 1 && setIndex(currentIndex => (currentIndex + 1) % imagePool.length);
  const fit = branding ? fitted[image] : null;
  const layer = branding && fit === undefined ? null : fit
    ? <motion.div key={image} className="service-image-layer" style={{ background: fit.background }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .6 }}>
      <img className="service-image-fitted" src={fit.src} alt={`${service.title} work`} />
    </motion.div>
    : <motion.div key={image} className="service-image-layer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .6 }}>
      {service.visual !== "web" && <img className="service-image-blur" src={image} alt="" aria-hidden="true" />}
      <img className="service-image-main" src={image} alt={`${service.title} work`} onError={skipFailed} />
    </motion.div>;

  if (service.visual === "web") return <div ref={frameRef} className="service-image-rotator service-image-browser"><div className="service-image-browser-bar"><i /><i /><i /><span /></div><div className="service-image-browser-page"><AnimatePresence initial={false}>{layer}</AnimatePresence></div></div>;
  return <div ref={frameRef} className={`service-image-rotator service-image-${service.visual}`}><AnimatePresence initial={false}>{layer}</AnimatePresence></div>;
}

function StatusMark({ yes }: { yes: boolean }) {
  return <span className={`status-mark ${yes ? "yes" : "no"}`} aria-label={yes ? "Included" : "Not included"}>{yes ? "✓" : "✕"}</span>;
}

function ComparisonSection() {
  const competitors = [
    { label: "In-house", long: "In-house team", valueIndex: 2 },
    { label: "Freelancers", long: "Freelancers", valueIndex: 3 },
    { label: "Agencies", long: "Typical agencies", valueIndex: 4 },
  ] as const;
  const [selected, setSelected] = useState(0);
  const competitor = competitors[selected];

  return <section className="section comparison-section" id="compare">
    <SectionTitle eyebrow="The clear difference" title={<>Why businesses choose <em>Mark&amp;Ting.</em></>}><p>See how one connected team compares when content, ads, websites, and follow-up need to work together.</p></SectionTitle>
    <Reveal>
      <div className="comparison-desktop">
        <table>
          <thead><tr><th>What you get</th><th className="mark-column"><strong>Mark&amp;Ting.</strong></th><th>In-house team</th><th>Freelancers</th><th>Typical agencies</th></tr></thead>
          <tbody>{comparisonRows.map(row => <tr key={row[0]}><th scope="row">{row[0]}</th>{row.slice(1).map((value, index) => <td key={index} className={index === 0 ? "mark-column" : ""}><StatusMark yes={Boolean(value)} /></td>)}</tr>)}
            <tr className="comparison-cta-row"><td /><td className="mark-column"><Button href="#contact" light>Book Strategy Call</Button></td><td /><td /><td /></tr>
          </tbody>
        </table>
      </div>
      <div className="comparison-mobile">
        <div className="comparison-chips" role="tablist" aria-label="Choose a comparison">{competitors.map((option, index) => <button key={option.label} type="button" role="tab" aria-selected={selected === index} onClick={() => setSelected(index)}>vs. {option.label}</button>)}</div>
        <div className="mobile-compare-head"><strong>Mark&amp;Ting.</strong><span>{competitor.long}</span></div>
        <div className="mobile-compare-list">{comparisonRows.map(row => <article key={row[0]}><h3>{row[0]}</h3><div><StatusMark yes={Boolean(row[1])} /><StatusMark yes={Boolean(row[competitor.valueIndex])} /></div></article>)}</div>
        <Button href="#contact">Book Strategy Call</Button>
      </div>
    </Reveal>
  </section>;
}

function WallClip({ clip, active }: { clip: Clip; active: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (active) video.play().catch(() => undefined);
    else {
      video.pause();
      video.currentTime = 0;
      video.removeAttribute("src");
      video.load();
    }
    return () => video.pause();
  }, [active]);
  return <div className="work-wall-card"><video ref={ref} src={active ? clip.mp4 : undefined} poster={clip.poster} muted loop playsInline preload="none" aria-hidden="true" tabIndex={-1} onCanPlay={() => active && ref.current?.play().catch(() => undefined)} /></div>;
}

function WorkWall() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { amount: .12 });
  const reduce = useReducedMotion();
  const clips = useClips();
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 620px)").matches);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 620px)");
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  // 8 different clips on desktop, 4 on phones (~300 KB each, loaded only while the wall is on screen).
  const uniqueCount = mobile ? 4 : 8;
  const start = mobile ? 5 : 10;
  const available = clips ?? [];
  const selected = available.slice(start, start + uniqueCount);
  const unique = selected.length === uniqueCount ? selected : available.slice(-uniqueCount);
  const wallClips = [...unique, ...unique];
  const active = Boolean(inView && !reduce);
  // No clips (the list failed to load): no wall rather than an empty band.
  if (clips !== null && !clips.length) return null;

  return <section ref={ref} className="section work-wall-section" id="work-wall">
    <SectionTitle eyebrow="Scroll-stopping media" title={<>Content that stops the scroll <em>and drives real revenue.</em></>}><p>A snapshot of the high-retention content we produce for real businesses every week.</p></SectionTitle>
    <Reveal><div className="work-wall-window">{wallClips.length ? <div className="work-wall-track">{wallClips.map((clip, index) => <WallClip key={`${clip.id}-${index}`} clip={clip} active={active} />)}</div> : <div className="work-wall-fallback" aria-hidden="true" />}</div></Reveal>
    <Reveal className="work-wall-action"><Button href={PORTFOLIO_URL}>See the full portfolio</Button></Reveal>
  </section>;
}

const industries = [
  ["#Restaurants & Hospitality", -8],
  ["#RealEstate & Lending", 6],
  ["#Automotive & Repair", -12],
  ["#Healthcare & Clinics", 10],
  ["#Home & Industrial Services", -6],
  ["#Faith & Community", 14],
  ["#B2B Professional Services", -4],
] as const;

function IndustriesSection() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, .5, 1], [.88, 1, .94]);
  const tagUp = useTransform(scrollYProgress, [0, .5, 1], [34, 0, -18]);
  const tagDown = useTransform(scrollYProgress, [0, .5, 1], [-24, 0, 24]);

  return <section ref={ref} className="industries-section" id="industries">
    <motion.div className="industries-card" style={{ scale: reduce ? 1 : scale }}>
      <Reveal className="industries-head"><Pill>Tailored solutions</Pill><h2>Deep expertise across <em>high-impact industries.</em></h2><p>We understand the specific client journeys and sales cycles of your industry.</p></Reveal>
      <div className="industry-tags">{industries.map(([label, rotate], index) => <motion.span key={label} style={{ y: reduce ? 0 : index % 2 ? tagDown : tagUp, rotate: reduce ? 0 : rotate }} initial={reduce ? false : { opacity: 0, scale: .8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: .5 }} transition={{ delay: index * .05, duration: .5 }}>{label}</motion.span>)}</div>
      <Button href="#contact" light>See how we grow your industry</Button>
    </motion.div>
  </section>;
}

function Header() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  return <header className="site-header" id="header">
    <a className="brand" href="#header">Mark&amp;Ting.</a>
    <nav className="desktop-nav" aria-label="Primary navigation">
      <a href="#services">Services</a>
      <a href="#benefits">Why us</a>
      <a href={PORTFOLIO_URL}>Work</a>
      <a href="#reviews">Reviews</a>
    </nav>
    <Button href="#contact">Book a call</Button>
    <motion.button className="menu-button" type="button" aria-expanded={open} aria-controls="site-menu" onClick={() => setOpen(v => !v)} whileTap={{ scale: .96 }}>
      Menu <motion.span animate={{ rotate: open ? 45 : 0 }}>＋</motion.span>
    </motion.button>
    <AnimatePresence>
      {open && <>
        <motion.button aria-label="Close menu" className="menu-backdrop" onClick={() => setOpen(false)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
        <motion.nav className="site-menu" id="site-menu" aria-label="Main navigation" initial={{ opacity: 0, y: -18, scale: .96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -12, scale: .97 }} transition={{ duration: .32, ease: [.22, 1, .36, 1] }} style={{ transformOrigin: "top right" }}>
          <div className="site-menu-head"><span>/ Explore</span><small>Mark&amp;Ting / 2026</small></div>
          <div className="site-menu-links">{nav.map(([label, id], index) => <motion.a key={id} href={`#${id}`} onClick={() => setOpen(false)} initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: .035 * index }}>{label}<span>↗</span></motion.a>)}</div>
        </motion.nav>
      </>}
    </AnimatePresence>
  </header>;
}

type PortfolioItem = {
  id: number;
  category: string;
  title: string;
  image?: string | null;
  youtubeId?: string;
  clip?: string | null;
  clipPoster?: string | null;
};
type Clip = { id: string; mp4: string; poster: string };

// Asked for as soon as the page script runs, before React renders: the gallery's light clips (8 s, ~300 KB each)
// of the portfolio's Content Production videos, served by the portfolio server from R2 (docs/CONTRATO.md).
const portfolioItemsRequest: Promise<PortfolioItem[]> = fetch(PORTFOLIO_ITEMS, { headers: { Accept: "application/json" } })
  .then(response => (response.ok ? response.json() : null))
  .then(payload => payload?.items ?? [])
  .catch(() => []);
const clipsRequest: Promise<Clip[]> = portfolioItemsRequest
  .then(items => items
    .filter((item: { category: string; clip?: string | null; clipPoster?: string | null }) => item.category === "video" && item.clip && item.clipPoster)
    .map(item => ({ id: item.youtubeId ?? String(item.id), mp4: new URL(item.clip!, PORTFOLIO_ITEMS).toString(), poster: new URL(item.clipPoster!, PORTFOLIO_ITEMS).toString() })))
  .catch(() => []);

function usePortfolioItems() {
  const [items, setItems] = useState<PortfolioItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    portfolioItemsRequest.then(list => { if (!cancelled) setItems(list); });
    return () => { cancelled = true; };
  }, []);
  return items;
}

/** The clips of the gallery. null while loading, empty if the list can't load. */
function useClips() {
  const [clips, setClips] = useState<Clip[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    clipsRequest.then(list => { if (!cancelled) setClips([...new Map(list.map(clip => [clip.id, clip])).values()]); });
    return () => { cancelled = true; };
  }, []);
  return clips;
}

function shuffled<T>(list: T[]) {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// A clip shows for 5-7 s (it loops if it ever runs out), then the next one for its cell starts on top and fades in once it plays.
const CLIP_HOLD_MS = [5000, 7000];
const CLIP_FADE_MS = 600;
const CELLS = 5;

type ClipSlot = { clip: Clip; key: number; hold: number; shownAt?: number; leaving?: boolean };
type CellState = { poster: Clip | null; slots: ClipSlot[] };

/** A muted looping clip over its cell. It shows once it really plays; the gallery decides when it leaves. */
function GalleryClip({ clip, leaving, onShow, onFail, onBlocked, onGone }: { clip: Clip; leaving: boolean; onShow: () => void; onFail: () => void; onBlocked: () => void; onGone: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [shown, setShown] = useState(false);
  const handlers = useRef({ onShow, onFail, onBlocked, onGone });
  handlers.current = { onShow, onFail, onBlocked, onGone };
  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(() => handlers.current.onGone(), CLIP_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let started = false;
    const show = () => {
      if (started) return;
      started = true;
      window.clearTimeout(giveUp);
      setShown(true);
      handlers.current.onShow();
    };
    const giveUp = window.setTimeout(() => handlers.current.onFail(), 8000);
    video.muted = true;
    video.addEventListener("playing", show);
    // iPhone Low Power Mode and some browsers refuse to autoplay: the gallery then stays on the covers.
    video.play()?.catch(error => (error?.name === "NotAllowedError" ? handlers.current.onBlocked() : undefined));
    return () => {
      window.clearTimeout(giveUp);
      video.removeEventListener("playing", show);
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, []);
  return <video ref={ref} className={`gallery-clip${shown ? " is-playing" : ""}${leaving ? " is-leaving" : ""}`} src={clip.mp4} poster={clip.poster}
    muted loop playsInline preload="auto" disablePictureInPicture aria-hidden="true" tabIndex={-1} onError={() => handlers.current.onFail()} />;
}

// The loading screen in index.html stays until every visible gallery cell plays a video: at least 0.8 s, at most 6 s.
const SPLASH_MIN_MS = 800;
const SPLASH_MAX_MS = 6000;
let splashHidden = false;
function hideSplash(onHidden: () => void) {
  if (splashHidden) return;
  splashHidden = true;
  window.setTimeout(() => {
    document.getElementById("splash")?.classList.add("is-done");
    document.documentElement.classList.remove("is-loading");
    onHidden();
    window.dispatchEvent(new Event("mt:revealed"));
    window.setTimeout(() => document.getElementById("splash")?.remove(), 900);
  }, Math.max(0, SPLASH_MIN_MS - performance.now()));
}

function HeroGallery({ onReady }: { onReady: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  // Starts a little before the gallery scrolls in, so the first videos are ready when it shows.
  const near = useInView(ref, { margin: "0px 0px 600px 0px" });
  const nearNow = useRef(near);
  nearNow.current = near;
  const clips = useClips();
  // Only if there are no clips: the rotating photos.
  const fallback = clips !== null && clips.length === 0;
  const [blocked, setBlocked] = useState(false);
  const [phase, setPhase] = useState(0);
  const [photos] = useState(() => shuffled(gallery));
  const [cells, setCells] = useState<CellState[]>(() => Array.from({ length: CELLS }, () => ({ poster: null, slots: [] })));
  const cellsNow = useRef(cells);
  const update = (change: (current: CellState[]) => CellState[]) => {
    const next = change(cellsNow.current);
    if (next === cellsNow.current) return;
    cellsNow.current = next;
    setCells(next);
  };
  const patchSlot = (cell: number, key: number, change: (slot: ClipSlot) => ClipSlot | null) => update(current => {
    const at = current[cell].slots.findIndex(slot => slot.key === key);
    if (at < 0) return current;
    const slots = [...current[cell].slots];
    const slot = change(slots[at]);
    if (slot) slots[at] = slot;
    else slots.splice(at, 1);
    const next = [...current];
    next[cell] = { ...current[cell], slots };
    return next;
  });
  // Each visit plays a random set of clips (20 on desktop, 10 on phones, ~300 KB each), every one once in random order
  // before any repeats, which then come from the browser cache. Never a clip another cell is showing.
  const pool = useRef<Clip[]>([]);
  const bag = useRef<Clip[]>([]);
  const nextClip = (busy: Set<string>) => {
    for (let round = 0; round < 2; round++) {
      const index = bag.current.findIndex(clip => !busy.has(clip.id));
      if (index >= 0) return bag.current.splice(index, 1)[0];
      bag.current = shuffled(pool.current);
    }
    return null;
  };
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  useEffect(() => {
    if (!fallback) return;
    const id = window.setInterval(() => setPhase(p => (p + 1) % photos.length), 3800);
    return () => window.clearInterval(id);
  }, [fallback, photos]);

  // Each cell gets a different random clip's first frame right away; its first video is that same clip.
  useEffect(() => {
    if (!clips?.length) return;
    pool.current = shuffled(clips).slice(0, window.matchMedia("(max-width: 900px)").matches ? 10 : 20);
    bag.current = shuffled(pool.current);
    update(current => {
      const busy = new Set<string>();
      return current.map(cell => {
        const poster = nextClip(busy);
        if (poster) busy.add(poster.id);
        return { ...cell, poster };
      });
    });
  }, [clips]);

  // Every visible cell always plays a clip. When one has shown for a while, the next one for that cell starts on top
  // and fades in once it plays (at most 2 starting at once on desktop, 1 on phones, besides cells still empty).
  // Off with reduced motion, data saver or blocked autoplay (first frames only), in a hidden tab, or once the gallery scrolls out of view.
  useEffect(() => {
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (reduce || saveData || blocked || !near || !clips?.length) return;
    const startCap = window.matchMedia("(max-width: 900px)").matches ? 1 : 2;
    const step = () => {
      if (document.hidden || !ref.current) return;
      const onPage = [...ref.current.querySelectorAll<HTMLElement>(".gallery-cell")].map(cell => cell.getClientRects().length > 0);
      update(current => {
        const now = Date.now();
        let changed = false;
        const next = current.map(cell => ({ ...cell, slots: [...cell.slots] }));
        let starting = next.reduce((count, cell) => count + cell.slots.filter(slot => !slot.shownAt && !slot.leaving).length, 0);
        const busy = new Set(next.flatMap(cell => cell.slots.map(slot => slot.clip.id)));
        for (const index of shuffled(next.map((_, i) => i))) {
          const cell = next[index];
          if (!onPage[index] || cell.slots.some(slot => !slot.shownAt && !slot.leaving)) continue;
          const top = [...cell.slots].reverse().find(slot => slot.shownAt && !slot.leaving);
          if (top && (now - top.shownAt! < top.hold || starting >= startCap)) continue;
          const clip = !top && cell.poster && !busy.has(cell.poster.id) ? cell.poster : nextClip(busy);
          if (!clip) continue;
          const [low, high] = CLIP_HOLD_MS;
          cell.slots.push({ clip, key: now + index, hold: low + Math.random() * (high - low) });
          busy.add(clip.id);
          starting++;
          changed = true;
        }
        return changed ? next : current;
      });
    };
    step();
    const every = window.setInterval(step, 400);
    return () => {
      window.clearInterval(every);
      update(current => (current.some(cell => cell.slots.length) ? current.map(cell => ({ ...cell, slots: [] })) : current));
    };
  }, [reduce, near, clips, blocked]);

  // Tells the loading screen when every visible cell plays a video, or right away when there is nothing to wait for:
  // first frames only (reduced motion, data saver, blocked autoplay), photos (no clips), or a page opened away from the gallery.
  useEffect(() => {
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (reduce || saveData || fallback || blocked) { onReady(); return; }
    if (!ref.current) return;
    const onPage = [...ref.current.querySelectorAll<HTMLElement>(".gallery-cell")].map(cell => cell.getClientRects().length > 0);
    if (onPage.some(Boolean) && cells.every((cell, index) => !onPage[index] || cell.slots.some(slot => slot.shownAt && !slot.leaving))) onReady();
  }, [cells, reduce, fallback, blocked]);
  useEffect(() => {
    const timer = window.setTimeout(() => { if (!nearNow.current) onReady(); }, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  const showClip = (cell: number, key: number) => {
    update(current => {
      const slot = current[cell].slots.find(item => item.key === key);
      if (!slot) return current;
      const next = [...current];
      next[cell] = { poster: slot.clip, slots: current[cell].slots.map(item => (item.key === key ? { ...item, shownAt: Date.now() } : item)) };
      return next;
    });
    // Once it has faded in, the clip underneath goes.
    window.setTimeout(() => update(current => {
      const at = current[cell].slots.findIndex(item => item.key === key);
      if (at <= 0) return current;
      const next = [...current];
      next[cell] = { ...current[cell], slots: current[cell].slots.slice(at) };
      return next;
    }), CLIP_FADE_MS + 100);
  };

  return <motion.div ref={ref} className="hero-gallery" style={{ y }} aria-label="Mark&Ting production work">
    {cells.map((cell, index) => <div className={`gallery-cell g${index + 1}`} key={index}>
      {fallback
        ? <AnimatePresence mode="popLayout">
          <motion.img key={photos[(phase + index) % photos.length]} src={`${A}${photos[(phase + index) % photos.length]}`} alt="Mark&Ting production work" initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: .9, ease: "easeOut" }} />
        </AnimatePresence>
        : cell.poster && <img src={cell.poster.poster} alt="" />}
      {cell.slots.map(slot => <GalleryClip key={slot.key} clip={slot.clip} leaving={Boolean(slot.leaving)}
        onShow={() => showClip(index, slot.key)}
        onFail={() => patchSlot(index, slot.key, item => ({ ...item, leaving: true }))}
        onBlocked={() => setBlocked(true)}
        onGone={() => patchSlot(index, slot.key, () => null)} />)}
    </div>)}
  </motion.div>;
}

function App() {
  const [faq, setFaq] = useState(0);
  const [contactStatus, setContactStatus] = useState({ state: "idle", message: "" });
  const portfolioItems = usePortfolioItems();
  // The hero plays its entrance once the loading screen lifts.
  const [revealed, setRevealed] = useState(false);
  const reveal = () => hideSplash(() => setRevealed(true));
  useEffect(() => {
    const timer = window.setTimeout(reveal, Math.max(0, SPLASH_MAX_MS - performance.now()));
    return () => window.clearTimeout(timer);
  }, []);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    // No scrolling under the loading screen.
    if (document.documentElement.classList.contains("is-loading")) lenis.stop();
    // When the loading screen lifts: the top of the page, or the section the link asked for (e.g. /#contact).
    const start = () => {
      lenis.start();
      const target = window.location.hash.length > 1 ? document.getElementById(decodeURIComponent(window.location.hash.slice(1))) : null;
      if (target) window.setTimeout(() => lenis.scrollTo(target, { offset: -90 }), 150);
      else lenis.scrollTo(0, { immediate: true });
    };
    window.addEventListener("mt:revealed", start);
    let frame = 0;
    const raf = (time: number) => { lenis.raf(time); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); window.removeEventListener("mt:revealed", start); lenis.destroy(); };
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const form = event.currentTarget;
    const subject = encodeURIComponent(`Strategy call request from ${data.get("name")}`);
    const body = encodeURIComponent(`Name: ${data.get("name")}\nEmail: ${data.get("email")}\nWebsite: ${data.get("website")}\nModel: ${data.get("model")}\n\n${data.get("message")}`);
    const mailto = `mailto:contact@markandtingagency.com?subject=${subject}&body=${body}`;

    setContactStatus({ state: "sending", message: "" });
    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          website: data.get("website"),
          model: data.get("model"),
          message: data.get("message"),
          company: data.get("company"),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.ok === false) throw new Error("Contact request failed.");
      form.reset();
      setContactStatus({ state: "success", message: "Thanks! We'll be in touch soon." });
    } catch {
      setContactStatus({ state: "error", message: "We could not send automatically. Opening your email app..." });
      window.location.href = mailto;
    }
  };

  return <>
    <ScrollProgress />
    <a className="announcement" href="#contact"><span>✦</span> Growth systems for businesses ready to scale <span>↗</span></a>
    <Header />
    <main>
      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-copy">
          <motion.div className="eyebrow-row" initial={{ opacity: 0, y: -10 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ duration: .7 }}><Pill>Content + Ads + Automation</Pill></motion.div>
          <motion.h1 id="hero-title" initial="hidden" animate={revealed ? "show" : "hidden"}><motion.span variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: .75, ease: [.22, 1, .36, 1] } } }}>Build attention.</motion.span><br /><motion.em variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: .75, delay: .12, ease: [.22, 1, .36, 1] } } }}>Turn it into revenue.</motion.em></motion.h1>
          <motion.p initial={{ opacity: 0, y: 25 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ duration: .8, delay: .15 }}>We help businesses grow with content, paid ads, automation, and AI systems that capture leads, follow up faster, and turn attention into revenue.</motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 20 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ delay: .28 }}><Button href="#contact">Book Strategy Call</Button><Button href={PORTFOLIO_URL} light>See Our Work</Button></motion.div>
          <motion.div className="trust-line" initial={{ opacity: 0 }} animate={revealed ? { opacity: 1 } : undefined} transition={{ delay: .55 }}><span className="avatar-stack"><img src={`${A}eU0eE1uH2s48zEZ2CvBgNbNmM1Y.jpeg`} alt="" /><img src={`${A}OS4kQqWVoAz7BhW5muuEOyaB6s.jpeg`} alt="" /><img src={`${A}LP3tY1VU63Loeiw2m8ahNTubMwQ.jpeg`} alt="" /></span><span><b>★★★★★</b><strong>Built for businesses that want more than marketing</strong><small>They want systems that grow with them.</small></span></motion.div>
        </div>
        <HeroGallery onReady={reveal} />
      </section>

      <section className="statement-section">
        <Reveal><p>Built for businesses that want more than marketing</p><h2>They want systems that grow with them.</h2></Reveal>
      </section>

      <section className="logo-section" aria-labelledby="logo-section-title">
        <Reveal><p id="logo-section-title">Trusted by businesses like yours</p></Reveal>
        <RandomLogoGrid />
      </section>

      <section className="section" id="services">
        <SectionTitle eyebrow="Services" title={<>What we can <em>do for you.</em></>}><p>Content, paid ads, websites, automation, and AI infrastructure built to attract qualified attention and turn interest into revenue.</p><strong>We do not just post content. We build a connected growth engine.</strong></SectionTitle>
        <div className="services-list">{services.map((service, i) => <Reveal key={service.title} delay={(i % 2) * .08}><TiltCard className={`service-card ${i % 2 ? "reverse" : ""}`} lift={-4}><div className="service-copy"><span className="service-badge">{service.badge}</span><h3>{service.title}</h3><p>{service.text}</p><ul>{service.bullets.map(item => <li key={item}>{item}</li>)}</ul><div className="service-actions"><Button href="#contact">Start now</Button>{(!portfolioItems.length || portfolioItems.some(item => item.category === service.work)) && <Button href={new URL(`#${service.work}`, PORTFOLIO_URL).toString()} light>See work</Button>}</div></div><div className={`service-image service-visual-${service.visual}`}><ServiceVisual service={service} items={portfolioItems} videoIndex={service.visual === "real-estate" ? 1 : 0} /></div></TiltCard></Reveal>)}</div>
      </section>

      <section className="system-band" aria-label="The Mark&Ting growth system">
        <Reveal className="system-band-head"><Pill>One connected system</Pill><h2>Strategy to execution.<br /><em>One team. One growth engine.</em></h2></Reveal>
        <div className="system-flow">
          <Reveal><article><span>01</span><h3>Content</h3><p>Build attention and trust with media that looks credible and worth choosing.</p></article></Reveal>
          <Reveal delay={.08}><article><span>02</span><h3>Ads</h3><p>Turn that attention into qualified demand through focused paid campaigns.</p></article></Reveal>
          <Reveal delay={.16}><article><span>03</span><h3>Automation</h3><p>Capture, qualify, and follow up before valuable opportunities go cold.</p></article></Reveal>
        </div>
      </section>

      <WorkWall />

      <section className="section" id="benefits">
        <SectionTitle eyebrow="The Mark&Ting advantage" title={<>More clients. More revenue. <em>Zero fluff.</em></>}><p>Creative media, paid distribution, and automated follow-up work together as one connected system.</p></SectionTitle>
        <div className="benefit-grid benefit-grid-v2">{benefits.map(([icon, title, text], i) => <Reveal key={title} delay={(i % 3) * .06}><motion.article whileHover={{ y: -6 }}><span className="benefit-icon">{icon}</span><h3>{title}</h3><p>{text}</p></motion.article></Reveal>)}</div>
        <Reveal className="benefit-actions"><Button href="#contact">Book a Strategy Call</Button><Button href={PORTFOLIO_URL} light>See Our Client Work</Button></Reveal>
      </section>

      <ComparisonSection />

      <IndustriesSection />

      <section className="section" id="pricing">
        <SectionTitle eyebrow="Pricing" title={<>Partnership<br />Packages.</>}><strong>Choose the level of support your business needs — from content consistency to a full client acquisition system.</strong></SectionTitle>
        <div className="pricing-grid">
          <Reveal><PriceCard title="Growth" text="Built for businesses ready to grow with consistent content, stronger visibility, and paid traffic." features={["Monthly content production", "Content editing + scheduling", "Instagram, Facebook & TikTok", "Branding optimization", "Meta Ads management", "Organic follower growth"]} /></Reveal>
          <Reveal delay={.08}><PriceCard dark title="Marketing 360" text="A complete growth system for businesses ready to combine content, ads, and automation." features={["Everything in Expansion", "Google Ads management", "Bot automation", "Increased content output", "Lead capture support", "Advanced growth support"]} /></Reveal>
        </div>
        <div className="process-grid">{[["⚙", "Strategy", "Business audit to identify opportunities and your best path to growth."], ["☷", "Build", "We create the content, campaigns, and systems your business needs."], ["✓", "Optimize", "Track performance, improve, and keep your growth system moving forward."]].map(([icon, title, text], i) => <Reveal key={title} delay={i * .07}><TiltCard lift={-5}><span>{icon}</span><h3>{title}</h3><p>{text}</p></TiltCard></Reveal>)}</div>
      </section>

      <section className="section" id="reviews">
        <SectionTitle eyebrow="Reviews" title={<>What Clients<br />Value Most.</>}><strong>Built for businesses that want more than marketing — they want systems that help them grow with consistency.</strong></SectionTitle>
        <Reveal><div className="rating-panel"><strong>4.9/5</strong><p>Content, ads, and automation designed to help businesses grow with more clarity and consistency.</p><Button href="#contact" light>Book Strategy Call</Button><div className="rating-tag"><span>★★★★★</span><br />Built for businesses that want more than marketing<br /><b>They want systems that grow with them.</b></div></div></Reveal>
        <div className="review-grid">{reviews.map(([quote, person, role, image], i) => <Reveal key={person} delay={(i % 2) * .07}><article><div className="quote"><span>4.9 <i>★</i> Rating</span><blockquote>“{quote}”</blockquote></div><div className="person"><img src={`${A}${image}`} alt={person} /><p><strong>{person}</strong><small>{role}</small></p></div></article></Reveal>)}</div>
      </section>

      <section className="section" id="team">
        <SectionTitle eyebrow="The people behind the results" title={<>The team behind <em>your growth.</em></>}><p>Strategy, content, design and technology — built by the people you work with directly.</p></SectionTitle>
        <div className="team-grid">{team.map((member, index) => <TeamMember key={member.name} {...member} delay={(index % 3) * .06} />)}</div>
      </section>

      <section className="section faq-section" id="faqs">
        <SectionTitle eyebrow="FAQs" title="Frequently Asked Questions."><p>Everything you need to know about working with Mark &amp; Ting and how we help businesses grow.</p></SectionTitle>
        <div className="faq-list">{faqs.map(([question, answer], i) => <Reveal key={question}><article className={faq === i ? "open" : ""}><button onClick={() => setFaq(faq === i ? -1 : i)} aria-expanded={faq === i}><span>{question}</span><motion.b aria-hidden="true" animate={{ rotate: faq === i ? 45 : 0 }}>＋</motion.b></button><AnimatePresence initial={false}>{faq === i && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}><p>{answer}</p></motion.div>}</AnimatePresence></article></Reveal>)}</div>
      </section>

      <section className="section contact-section" id="contact">
        <SectionTitle eyebrow="Contact" title={<>Stop Losing Revenue<br />To Inconsistent Marketing<br />And Outdated Systems.</>}><strong>Let’s talk about the right system for you!</strong></SectionTitle>
        <Reveal><div className="contact-card"><div className="contact-details"><div><small>Email us</small><a href="mailto:contact@markandtingagency.com">contact@markandtingagency.com</a></div><div><small>Call or text</small><a href="tel:+19185807197">+1 918 580 7197</a></div></div><form onSubmit={submit}><label>Name<input name="name" autoComplete="name" placeholder="Your name" required /></label><label>E-mail*<input type="email" name="email" autoComplete="email" placeholder="Your email" required /></label><label>Website<input type="url" name="website" placeholder="Your website" /></label><label className="contact-honeypot" aria-hidden="true">Company<input name="company" tabIndex={-1} autoComplete="off" /></label><fieldset><legend>Pricing model</legend><label><input type="radio" name="model" value="Growth Support" defaultChecked /> Growth Support</label><label><input type="radio" name="model" value="One-Time" /> One-Time</label></fieldset><label>Message<textarea name="message" placeholder="Your message" rows={5} required /></label><motion.button type="submit" disabled={contactStatus.state === "sending"} whileHover={{ scale: 1.01 }} whileTap={{ scale: .98 }}>{contactStatus.state === "sending" ? "Sending..." : "Schedule Call"} <span>↗</span></motion.button>{contactStatus.message && <p className={`contact-status ${contactStatus.state === "error" ? "error" : ""}`}>{contactStatus.message}</p>}</form></div></Reveal>
      </section>

      <section className="section" id="onboarding">
        <SectionTitle eyebrow="Onboarding" title={<>Okay… But What Happens<br />After I Say Yes?</>} />
        <div className="steps-grid">{steps.map(([title, text], i) => <Reveal key={title} delay={(i % 2) * .08}><TiltCard lift={-6}><b>{String(i + 1).padStart(2, "0")}</b><h3>{title}</h3><p>{text}</p></TiltCard></Reveal>)}</div>
      </section>

    </main>
    <Footer />
  </>;
}

function PriceCard({ title, text, features, dark = false }: { title: string; text: string; features: string[]; dark?: boolean }) {
  return <TiltCard className={`price-card ${dark ? "dark" : "light"}`} lift={-5}><div className="price-intro"><span className="square-icon">{dark ? "▱" : "ϟ"}</span><h3>{title}</h3><p>{text}</p></div><ul>{features.map(feature => <li key={feature}>{feature}</li>)}</ul><Button href="#contact" light={dark}>Book Strategy Call</Button></TiltCard>;
}

function TeamMember({ image, name, role, bio, delay }: { image: string; name: string; role: string; bio: string; delay: number }) {
  return <Reveal delay={delay}><article className="team-card"><div className="team-image"><motion.img src={image} alt={name} whileHover={{ scale: 1.04 }} transition={{ duration: .55 }} /></div><div className="team-copy"><Pill>{role}</Pill><h3>{name}</h3><p>{bio}</p></div></article></Reveal>;
}

function Footer() {
  return <footer><div className="footer-top"><div><h2>Let’s Grow Your Business Smarter.</h2><p>Content, ads, and automation built to help Hispanic businesses and realtors grow with more consistency, visibility, and results.</p><a className="footer-email" href="mailto:contact@markandtingagency.com">Your Email <b>↗</b></a><a className="footer-phone" href="tel:+19185807197">+1 918 580 7197</a></div><div className="footer-links"><div><strong>/Explore</strong><a href="#services">Services</a><a href="#benefits">Benefits</a><a href={PORTFOLIO_URL}>Work</a><a href="#pricing">Pricing</a><a href="#reviews">Reviews</a><a href="#faqs">FAQs</a></div><div><strong>/Resources</strong><a href="#header">All Projects</a><a href="/legals/privacy-policy/">Privacy Policy</a><a href="/legals/terms-of-service/">Terms of Service</a></div><div><strong>/Socials</strong><a href="https://www.tiktok.com/@markandtingagency">TikTok</a><a href="https://www.facebook.com/people/MarkTing-Growth-Agency/61583170465614/">Facebook</a><a href="https://www.instagram.com/markandtingagency">Instagram</a><a href="https://www.linkedin.com/company/markandtingagency/">LinkedIn</a><a href="https://linktr.ee/MarkandTingAgency">linktr.ee</a></div></div></div><Reveal><div className="footer-wordmark">Mark&amp;Ting<span>.</span></div></Reveal><div className="copyright">© 2026 Mark&amp;Ting. All rights reserved.</div></footer>;
}

export default App;
